import { useRef, useState } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import {
  X, HardDrive, FileText, Image, Search, Loader2, File, Download,
} from 'lucide-react'
import { listDriveFiles, importDriveFiles, DriveFile } from '../../shared/api/drive'

interface Props {
  tripId: string
  onClose: () => void
  onImported?: (docs: number, media: number) => void
}

export default function DriveImportPanel({ tripId, onClose, onImported }: Props) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())

  const { data: files = [], isLoading, isError } = useQuery({
    queryKey: ['drive-files', tripId, debouncedQ],
    queryFn: () => listDriveFiles(tripId, debouncedQ || undefined),
  })

  const importMut = useMutation({
    mutationFn: (ids: string[]) => importDriveFiles(tripId, ids),
    onSuccess: (imported) => {
      const docs  = imported.filter((d) => !d.is_media).length
      const media = imported.filter((d) => d.is_media).length
      onImported?.(docs, media)
      onClose()
    },
  })

  const drvDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleSearch = (v: string) => {
    setSearchTerm(v)
    if (drvDebounceRef.current) clearTimeout(drvDebounceRef.current)
    drvDebounceRef.current = setTimeout(() => setDebouncedQ(v), 400)
  }

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const allIds = files.map((f) => f.file_id)
  const allSelected = allIds.length > 0 && allIds.every((id) => selected.has(id))
  const toggleAll = () =>
    setSelected(allSelected ? new Set() : new Set(allIds))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-lg bg-white rounded-2xl shadow-2xl flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div className="flex items-center gap-2 text-gray-900 font-semibold">
            <HardDrive className="w-5 h-5 text-yellow-500" />
            Import from Google Drive
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Search */}
        <div className="px-4 py-3 border-b">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search files… (e.g. confirmation, booking)"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-400"
            />
          </div>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading && (
            <div className="flex items-center justify-center h-32 text-gray-400">
              <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading files…
            </div>
          )}
          {isError && (
            <p className="text-center text-sm text-red-500 py-8">
              Could not load Drive files. Is your Google account connected?
            </p>
          )}
          {!isLoading && !isError && files.length === 0 && (
            <p className="text-center text-sm text-gray-400 py-8">
              {debouncedQ ? `No files matching "${debouncedQ}"` : 'No travel files found in Drive.'}
            </p>
          )}
          {!isLoading && files.length > 0 && (
            <>
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs text-gray-500">{files.length} files found</span>
                <button
                  className="text-xs text-yellow-600 hover:underline"
                  onClick={toggleAll}
                >
                  {allSelected ? 'Deselect all' : 'Select all'}
                </button>
              </div>
              <ul className="space-y-2">
                {files.map((f) => (
                  <DriveFileRow
                    key={f.file_id}
                    file={f}
                    checked={selected.has(f.file_id)}
                    onToggle={() => toggle(f.file_id)}
                  />
                ))}
              </ul>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-5 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
          >
            Cancel
          </button>
          <button
            disabled={selected.size === 0 || importMut.isPending}
            onClick={() => importMut.mutate([...selected])}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-yellow-500 text-white hover:bg-yellow-600 disabled:opacity-50"
          >
            {importMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            <Download className="w-4 h-4" />
            Import {selected.size > 0 ? `${selected.size} file${selected.size > 1 ? 's' : ''}` : ''}
          </button>
        </div>
      </div>
    </div>
  )
}

function formatSize(bytes: number | null): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function FileIcon({ mime }: { mime: string }) {
  if (mime.startsWith('image/')) return <Image className="w-4 h-4 text-blue-400" />
  if (mime.includes('pdf'))       return <FileText className="w-4 h-4 text-red-400" />
  return <File className="w-4 h-4 text-gray-400" />
}

function DriveFileRow({ file, checked, onToggle }: {
  file: DriveFile
  checked: boolean
  onToggle: () => void
}) {
  return (
    <li
      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition ${
        checked ? 'border-yellow-400 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
      }`}
      onClick={onToggle}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        onClick={(e) => e.stopPropagation()}
        className="accent-yellow-500"
        aria-label={`Select ${file.name}`}
      />
      <FileIcon mime={file.mime_type} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
        <p className="text-xs text-gray-400">
          {file.modified_at && `${file.modified_at}  ·  `}
          {formatSize(file.size_bytes)}
        </p>
      </div>
    </li>
  )
}
